/**
 * StaffSettings — Coach settings page with availability, blocked time, and slot preview.
 * Replaces the "coming soon" placeholder for /staff/settings.
 */

import { useState, useEffect, useCallback } from 'react';
import { Clock, CalendarOff, Plus, Trash2, ToggleLeft, ToggleRight, ChevronDown, Cloud, RefreshCw, Unlink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Modal } from '../admin/Modal';
import { AvailabilityGrid } from './AvailabilityGrid';

// --- Types ---

interface AvailabilityWindow {
  id: string;
  coach_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  services: string[];
  is_active: boolean;
  effective_date: string | null;
  expiry_date: string | null;
}

interface Block {
  id: string;
  coach_id: string;
  start_time: string;
  end_time: string;
  reason: string;
  type: string;
  all_day: boolean;
}

interface Service {
  id: string;
  name: string;
  duration_minutes: number;
  is_active: boolean;
}

interface Slot {
  start_time: string;
  end_time: string;
  coach_id: string;
  service_id: string;
}

// DAYS/DAY_ABBR moved to AvailabilityGrid

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// formatTime removed — now handled by AvailabilityGrid component

// Format ISO timestamp to display
const formatDateTime = (iso: string) => {
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr + 'T12:00:00Z');
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

// Generate time options in 15-min increments (HH:MM)
const TIME_SLOTS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_SLOTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

/**
 * Convert HH:MM from one timezone to another using a reference date.
 * We use a fixed reference Monday to get consistent UTC offset.
 */
function convertTime(time: string, fromTZ: string, toTZ: string): string {
  const [h, m] = time.split(':').map(Number);
  // Use today's date to get correct DST offset
  const today = new Date();
  const refDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0));

  if (fromTZ === 'UTC') {
    refDate.setUTCHours(h, m, 0, 0);
    const parts = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: toTZ }).formatToParts(refDate);
    const hh = parts.find(p => p.type === 'hour')?.value || '00';
    const mm = parts.find(p => p.type === 'minute')?.value || '00';
    return `${hh}:${mm}`;
  } else {
    // fromTZ is local, convert to UTC — find which UTC slot displays as the given local time
    for (const slot of TIME_SLOTS) {
      const [sh, sm] = slot.split(':').map(Number);
      const test = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), sh, sm, 0));
      const parts = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: fromTZ }).formatToParts(test);
      const localH = parts.find(p => p.type === 'hour')?.value || '';
      const localM = parts.find(p => p.type === 'minute')?.value || '';
      if (`${localH}:${localM}` === time) return slot;
    }
    return time;
  }
}

/** Format HH:MM for display in a given timezone (to show in dropdown labels) */
function formatTimeInTZ(utcTime: string, tz: string): string {
  const [h, m] = utcTime.split(':').map(Number);
  const today = new Date();
  const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0));
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
}

// Pre-compute local time labels for all 96 time slots (cached per timezone)
// Cache key includes date to invalidate on DST transitions
const localTimeCache = new Map<string, { value: string; label: string }[]>();
function getLocalTimeOptions(tz: string): { value: string; label: string }[] {
  if (localTimeCache.has(tz)) return localTimeCache.get(tz)!;
  const options = TIME_SLOTS.map(t => ({
    value: t,
    label: formatTimeInTZ(convertTime(t, tz, 'UTC'), tz)
  }));
  localTimeCache.set(tz, options);
  return options;
}

export function StaffSettings() {
  const { user } = useAuth();
  const coachId = user?.id || '';
  const coachTZ = user?.timezone || 'America/New_York';
  const localTimeOpts = getLocalTimeOptions(coachTZ);

  const [activeTab, setActiveTab] = useState<'availability' | 'blocked' | 'preview' | 'calendar-sync'>('availability');
  const [services, setServices] = useState<Service[]>([]);

  // Availability state
  const [windows, setWindows] = useState<AvailabilityWindow[]>([]);
  const [loadingAvail, setLoadingAvail] = useState(true);

  // Blocked time state
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [loadingBlocks, setLoadingBlocks] = useState(true);

  // DEPRECATED: Slot Preview — removed from UI (tab hidden). Slot preview will be
  // replaced by the client booking widget in Phase 5. Remove this block during code cleanup.
  const [previewServiceId, setPreviewServiceId] = useState('');
  const [previewResults, setPreviewResults] = useState<{ date: string; slots: Slot[] }[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Calendar sync state
  const [syncStatus, setSyncStatus] = useState<{
    connected: boolean; provider: string | null; last_sync: string | null;
    calendars: Record<string, { name: string; sync_enabled: boolean; is_mc_calendar: boolean }> | null;
  } | null>(null);
  const [loadingSync, setLoadingSync] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Apple Calendar sync state
  const [appleSyncStatus, setAppleSyncStatus] = useState<{
    connected: boolean; provider: string | null; apple_id: string | null; last_sync: string | null;
    calendars: Record<string, { name: string; sync_enabled: boolean; is_mc_calendar: boolean; supports_events?: boolean }> | null;
  } | null>(null);
  const [loadingAppleSync, setLoadingAppleSync] = useState(true);
  const [syncingApple, setSyncingApple] = useState(false);
  const [appleConnectForm, setAppleConnectForm] = useState({ email: '', password: '' });
  const [connectingApple, setConnectingApple] = useState(false);
  const [appleConnectError, setAppleConnectError] = useState<string | null>(null);

  // Modal state (window modal now handled by AvailabilityGrid)


  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState<Block | null>(null);
  const [blockForm, setBlockForm] = useState({ start_date: '', start_time_val: '00:00', end_date: '', end_time_val: '23:59', reason: '', type: 'blocked', all_day: true });

  const [error, setError] = useState<string | null>(null);

  // --- Data fetching ---

  const fetchServices = useCallback(async () => {
    try {
      const res = await fetch('/api/services?active_only=true', { headers: getAuthHeaders() });
      const data = await res.json();
      setServices(data.services || []);
    } catch { /* ignore */ }
  }, []);

  const fetchAvailability = useCallback(async () => {
    if (!coachId) return;
    setLoadingAvail(true);
    try {
      const res = await fetch(`/api/availability/${coachId}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setWindows(data.windows || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingAvail(false);
    }
  }, [coachId]);

  const fetchBlocks = useCallback(async () => {
    if (!coachId) return;
    setLoadingBlocks(true);
    try {
      const res = await fetch(`/api/blocked-time/${coachId}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setBlocks(data.blocks || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingBlocks(false);
    }
  }, [coachId]);

  const fetchSyncStatus = useCallback(async () => {
    setLoadingSync(true);
    try {
      const res = await fetch('/api/integrations/calendar/status', { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSyncStatus(data);
      }
    } catch { /* ignore */ }
    finally { setLoadingSync(false); }
  }, []);

  const fetchAppleSyncStatus = useCallback(async () => {
    setLoadingAppleSync(true);
    try {
      const res = await fetch('/api/integrations/apple-calendar/status', { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAppleSyncStatus(data);
      }
    } catch { /* ignore */ }
    finally { setLoadingAppleSync(false); }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchAvailability();
    fetchBlocks();
    fetchSyncStatus();
    fetchAppleSyncStatus();
  }, [fetchServices, fetchAvailability, fetchBlocks, fetchSyncStatus, fetchAppleSyncStatus]);

  // --- Availability Windows ---

  // Old list-based window functions removed — now handled by AvailabilityGrid

  // --- Overrides ---

  // --- Blocked Time ---

  const openCreateBlock = () => {
    setEditingBlock(null);
    setBlockForm({ start_date: '', start_time_val: '00:00', end_date: '', end_time_val: '23:59', reason: '', type: 'blocked', all_day: true });
    setShowBlockModal(true);
  };

  const saveBlock = async () => {
    setError(null);
    try {
      const startTime = blockForm.all_day
        ? `${blockForm.start_date}T00:00:00Z`
        : `${blockForm.start_date}T${blockForm.start_time_val}:00Z`;
      const endTime = blockForm.all_day
        ? `${blockForm.end_date || blockForm.start_date}T23:59:59Z`
        : `${blockForm.end_date || blockForm.start_date}T${blockForm.end_time_val}:00Z`;

      const body = { start_time: startTime, end_time: endTime, reason: blockForm.reason, type: blockForm.type, all_day: blockForm.all_day };

      const url = editingBlock ? `/api/blocked-time/${coachId}/${editingBlock.id}` : `/api/blocked-time/${coachId}`;
      const method = editingBlock ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setShowBlockModal(false);
      fetchBlocks();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteBlock = async (b: Block) => {
    await fetch(`/api/blocked-time/${coachId}/${b.id}`, { method: 'DELETE', headers: getAuthHeaders() });
    fetchBlocks();
  };

  // --- DEPRECATED: Slot Preview (tab removed, code kept for reference) ---
  // Remove this block and the SLOT PREVIEW TAB section below during code cleanup.

  const fetchSlotPreview = async () => {
    if (!previewServiceId) return;
    setLoadingPreview(true);
    try {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      const endDate = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const res = await fetch(`/api/availability/slots?service_id=${previewServiceId}&coach_id=${coachId}&start_date=${startDate}&end_date=${endDate}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setPreviewResults(data.results || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (previewServiceId && activeTab === 'preview') fetchSlotPreview();
  }, [previewServiceId, activeTab]); // fetchSlotPreview intentionally omitted — only trigger on service/tab change



  return (
    <div className="staff-settings">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-tabs">
        <button className={`settings-tab ${activeTab === 'availability' ? 'active' : ''}`} onClick={() => setActiveTab('availability')}>
          <Clock size={16} /> Availability
        </button>
        <button className={`settings-tab ${activeTab === 'blocked' ? 'active' : ''}`} onClick={() => setActiveTab('blocked')}>
          <CalendarOff size={16} /> Blocked Time
        </button>
        <button className={`settings-tab ${activeTab === 'calendar-sync' ? 'active' : ''}`} onClick={() => setActiveTab('calendar-sync')}>
          <Cloud size={16} /> Calendar Sync
        </button>
      </div>

      {error && <div className="settings-error">{error} <button onClick={() => setError(null)}>dismiss</button></div>}

      {/* ==================== AVAILABILITY TAB ==================== */}
      {activeTab === 'availability' && (
        <div className="settings-section">
          <div className="section-header">
            <div>
              <h2>Weekly Availability</h2>
              <p className="section-desc">Click and drag on the grid to create windows. Click existing blocks to edit.</p>
            </div>
          </div>

          {loadingAvail ? <div className="settings-loading">Loading...</div> : (
            <AvailabilityGrid windows={windows} blocks={blocks} services={services} onRefresh={() => { fetchAvailability(); fetchBlocks(); }} />
          )}

        </div>
      )}

      {/* ==================== BLOCKED TIME TAB ==================== */}
      {activeTab === 'blocked' && (
        <div className="settings-section">
          <div className="section-header">
            <div>
              <h2>Do Not Schedule</h2>
              <p className="section-desc">Block time for vacation, personal commitments, or anything that overrides all availability.</p>
            </div>
            <button className="btn btn-primary" onClick={openCreateBlock}><Plus size={16} /> Add Block</button>
          </div>

          {loadingBlocks ? <div className="settings-loading">Loading...</div> : blocks.length === 0 ? (
            <p className="empty-message">No blocked time set.</p>
          ) : (
            <div className="blocks-list">
              {blocks.map(b => (
                <div key={b.id} className="block-row">
                  <span className={`block-type-badge ${b.type}`}>{b.type}</span>
                  <span className="block-dates">
                    {b.all_day
                      ? `${formatDate(b.start_time.split('T')[0])}${b.start_time.split('T')[0] !== b.end_time.split('T')[0] ? ` - ${formatDate(b.end_time.split('T')[0])}` : ''}`
                      : `${formatDateTime(b.start_time)} - ${formatDateTime(b.end_time)}`
                    }
                  </span>
                  {b.reason && <span className="block-reason">{b.reason}</span>}
                  <button className="icon-btn" onClick={() => deleteBlock(b)}><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== DEPRECATED: SLOT PREVIEW TAB ====================
          Tab button removed. This section is unreachable. Will be replaced by
          client booking widget (Phase 5). Remove during code cleanup.
          ================================================================== */}
      {activeTab === 'preview' && (
        <div className="settings-section">
          <div className="section-header">
            <div>
              <h2>Slot Preview</h2>
              <p className="section-desc">See your available booking slots for the next 7 days based on your current settings.</p>
            </div>
          </div>

          <div className="preview-controls">
            <label>Service:</label>
            <div className="select-wrapper">
              <select value={previewServiceId} onChange={e => setPreviewServiceId(e.target.value)}>
                <option value="">Select a service...</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes}min)</option>
                ))}
              </select>
              <ChevronDown size={14} className="select-icon" />
            </div>
            <button className="btn btn-secondary" onClick={fetchSlotPreview} disabled={!previewServiceId || loadingPreview}>
              {loadingPreview ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {previewResults.length === 0 && previewServiceId && !loadingPreview && (
            <p className="empty-message">No available slots found for the next 7 days. Check your availability windows.</p>
          )}

          {previewResults.map(day => (
            <div key={day.date} className="preview-day">
              <div className="preview-day-header">{formatDate(day.date)}</div>
              <div className="preview-slots">
                {day.slots.map((s, i) => (
                  <span key={i} className="slot-chip">
                    {new Date(s.start_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ==================== CALENDAR SYNC TAB ==================== */}
      {activeTab === 'calendar-sync' && (
        <div className="settings-section">
          <div className="section-header">
            <div>
              <h2>Calendar Sync</h2>
              <p className="section-desc">Connect your Google Calendar to sync availability and appointments.</p>
            </div>
          </div>

          {loadingSync ? <div className="settings-loading">Loading...</div> : !syncStatus?.connected ? (
            <div className="sync-disconnected">
              <div className="sync-icon"><Cloud size={48} /></div>
              <h3>Connect Google Calendar</h3>
              <p>Sync your personal calendar events to automatically block availability, and push MC appointments to a dedicated Mission Control calendar on your Google account.</p>
              <button className="btn btn-primary connect-btn" onClick={async () => {
                const res = await fetch('/api/integrations/calendar/auth-url', { headers: getAuthHeaders() });
                const data = await res.json();
                if (data.authUrl) window.location.href = data.authUrl;
                else setError(data.error || 'Failed to start OAuth');
              }}>
                <Cloud size={16} /> Connect Google Calendar
              </button>
            </div>
          ) : (
            <div className="sync-connected">
              <div className="sync-status-bar">
                <span className="sync-badge connected">Connected</span>
                <span className="sync-provider">Google Calendar</span>
                {syncStatus.last_sync && (
                  <span className="sync-last">Last sync: {new Date(syncStatus.last_sync).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                )}
                <div className="sync-actions-bar">
                  <button className="btn btn-secondary" onClick={async () => {
                    setSyncing(true);
                    await fetch('/api/integrations/calendar/sync', { method: 'POST', headers: getAuthHeaders() });
                    await fetchSyncStatus();
                    setSyncing(false);
                  }} disabled={syncing}>
                    <RefreshCw size={14} className={syncing ? 'spinning' : ''} /> {syncing ? 'Syncing...' : 'Sync Now'}
                  </button>
                  <button className="btn btn-secondary danger-text" onClick={async () => {
                    if (!confirm('Disconnect Google Calendar? This will stop all calendar syncing.')) return;
                    await fetch('/api/integrations/calendar/disconnect', { method: 'POST', headers: getAuthHeaders() });
                    await fetchSyncStatus();
                  }}>
                    <Unlink size={14} /> Disconnect
                  </button>
                </div>
              </div>

              {syncStatus.calendars && (
                <div className="calendar-list">
                  <h3>Calendars</h3>
                  <p className="section-desc">Toggle which calendars sync. Busy, tentative, and out-of-office events will block your availability.</p>
                  {Object.entries(syncStatus.calendars).map(([calId, cal]) => (
                    <div key={calId} className="calendar-item">
                      <div className="calendar-info">
                        <span className="calendar-name">{cal.name}</span>
                        {cal.is_mc_calendar && <span className="mc-badge">MC Calendar (bidirectional)</span>}
                      </div>
                      <button
                        className="icon-btn"
                        onClick={async () => {
                          if (cal.is_mc_calendar) return; // Can't disable MC calendar
                          await fetch('/api/integrations/calendar/preferences', {
                            method: 'PUT',
                            headers: getAuthHeaders(),
                            body: JSON.stringify({ calendars: { [calId]: { sync_enabled: !cal.sync_enabled } } })
                          });
                          fetchSyncStatus();
                        }}
                        title={cal.is_mc_calendar ? 'MC calendar is always synced' : cal.sync_enabled ? 'Disable sync' : 'Enable sync'}
                      >
                        {cal.sync_enabled ? <ToggleRight size={20} color="#10b981" /> : <ToggleLeft size={20} />}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ========== APPLE iCLOUD SECTION ========== */}
          <div className="section-header" style={{ marginTop: '2rem' }}>
            <div>
              <h2>Apple iCloud Calendar</h2>
              <p className="section-desc">Connect your iCloud calendar via app-specific password. Same sync model as Google: personal calendars block availability, MC appointments push to a dedicated "Mission Control" calendar.</p>
            </div>
          </div>

          {loadingAppleSync ? <div className="settings-loading">Loading...</div> : !appleSyncStatus?.connected ? (
            <div className="sync-disconnected">
              <div className="sync-icon"><Cloud size={48} /></div>
              <h3>Connect Apple iCloud</h3>
              <p>You need an <strong>app-specific password</strong> from Apple — your normal Apple ID password won't work. <a href="https://account.apple.com/account/manage" target="_blank" rel="noopener noreferrer">Generate one at account.apple.com</a> → Sign-In and Security → App-Specific Passwords. Label it "Mission Control".</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '420px', margin: '1rem auto' }}>
                <input
                  type="email"
                  placeholder="Apple ID email"
                  value={appleConnectForm.email}
                  onChange={(e) => setAppleConnectForm({ ...appleConnectForm, email: e.target.value })}
                  style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                />
                <input
                  type="password"
                  placeholder="App-specific password (xxxx-xxxx-xxxx-xxxx)"
                  value={appleConnectForm.password}
                  onChange={(e) => setAppleConnectForm({ ...appleConnectForm, password: e.target.value })}
                  style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                />
                {appleConnectError && (
                  <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{appleConnectError}</div>
                )}
                <button
                  className="btn btn-primary connect-btn"
                  disabled={connectingApple || !appleConnectForm.email || !appleConnectForm.password}
                  onClick={async () => {
                    setConnectingApple(true);
                    setAppleConnectError(null);
                    try {
                      const res = await fetch('/api/integrations/apple-calendar/connect', {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify(appleConnectForm)
                      });
                      const data = await res.json();
                      if (!res.ok) {
                        setAppleConnectError(data.error || 'Failed to connect');
                      } else {
                        setAppleConnectForm({ email: '', password: '' });
                        await fetchAppleSyncStatus();
                      }
                    } catch (err) {
                      setAppleConnectError((err as Error).message);
                    } finally {
                      setConnectingApple(false);
                    }
                  }}
                >
                  <Cloud size={16} /> {connectingApple ? 'Connecting...' : 'Connect Apple iCloud'}
                </button>
              </div>
            </div>
          ) : (
            <div className="sync-connected">
              <div className="sync-status-bar">
                <span className="sync-badge connected">Connected</span>
                <span className="sync-provider">iCloud ({appleSyncStatus.apple_id})</span>
                {appleSyncStatus.last_sync && (
                  <span className="sync-last">Last sync: {new Date(appleSyncStatus.last_sync).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                )}
                <div className="sync-actions-bar">
                  <button className="btn btn-secondary" onClick={async () => {
                    setSyncingApple(true);
                    await fetch('/api/integrations/apple-calendar/sync', { method: 'POST', headers: getAuthHeaders() });
                    await fetchAppleSyncStatus();
                    setSyncingApple(false);
                  }} disabled={syncingApple}>
                    <RefreshCw size={14} className={syncingApple ? 'spinning' : ''} /> {syncingApple ? 'Syncing...' : 'Sync Now'}
                  </button>
                  <button className="btn btn-secondary danger-text" onClick={async () => {
                    if (!confirm('Disconnect Apple Calendar? This will stop all calendar syncing and remove your stored credentials.')) return;
                    await fetch('/api/integrations/apple-calendar/disconnect', { method: 'POST', headers: getAuthHeaders() });
                    await fetchAppleSyncStatus();
                  }}>
                    <Unlink size={14} /> Disconnect
                  </button>
                </div>
              </div>

              {appleSyncStatus.calendars && (
                <div className="calendar-list">
                  <h3>Calendars</h3>
                  <p className="section-desc">Toggle which calendars sync. Busy and tentative events will block your availability. Task-only calendars are disabled by default.</p>
                  {Object.entries(appleSyncStatus.calendars).map(([calId, cal]) => (
                    <div key={calId} className="calendar-item">
                      <div className="calendar-info">
                        <span className="calendar-name">{cal.name}</span>
                        {cal.is_mc_calendar && <span className="mc-badge">MC Calendar (bidirectional)</span>}
                        {cal.supports_events === false && <span className="mc-badge" style={{ backgroundColor: '#9ca3af' }}>Tasks only</span>}
                      </div>
                      <button
                        className="icon-btn"
                        onClick={async () => {
                          if (cal.is_mc_calendar) return;
                          await fetch('/api/integrations/apple-calendar/preferences', {
                            method: 'PUT',
                            headers: getAuthHeaders(),
                            body: JSON.stringify({ calendars: { [calId]: { sync_enabled: !cal.sync_enabled } } })
                          });
                          fetchAppleSyncStatus();
                        }}
                        title={cal.is_mc_calendar ? 'MC calendar is always synced' : cal.sync_enabled ? 'Disable sync' : 'Enable sync'}
                      >
                        {cal.sync_enabled ? <ToggleRight size={20} color="#10b981" /> : <ToggleLeft size={20} />}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== BLOCK MODAL ==================== */}
      <Modal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        title={editingBlock ? 'Edit Blocked Time' : 'Block Time'}
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowBlockModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveBlock} disabled={!blockForm.start_date}>{editingBlock ? 'Update' : 'Create'}</button>
          </>
        }
      >
        <div className="modal-form">
          <div className="form-group">
            <label>Type</label>
            <select value={blockForm.type} onChange={e => setBlockForm(prev => ({ ...prev, type: e.target.value }))}>
              <option value="blocked">Blocked</option>
              <option value="vacation">Vacation</option>
              <option value="personal">Personal</option>
            </select>
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={blockForm.all_day} onChange={e => setBlockForm(prev => ({ ...prev, all_day: e.target.checked }))} />
            All Day
          </label>
          <div className="form-row-2">
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" value={blockForm.start_date} onChange={e => setBlockForm(prev => ({ ...prev, start_date: e.target.value, end_date: prev.end_date || e.target.value }))} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" value={blockForm.end_date} onChange={e => setBlockForm(prev => ({ ...prev, end_date: e.target.value }))} />
            </div>
          </div>
          {!blockForm.all_day && (
            <div className="form-row-2">
              <div className="form-group">
                <label>Start Time (UTC)</label>
                <select value={blockForm.start_time_val} onChange={e => setBlockForm(prev => ({ ...prev, start_time_val: e.target.value }))}>
                  {localTimeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>End Time (UTC)</label>
                <select value={blockForm.end_time_val} onChange={e => setBlockForm(prev => ({ ...prev, end_time_val: e.target.value }))}>
                  {localTimeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="form-group">
            <label>Reason (optional)</label>
            <input type="text" value={blockForm.reason} onChange={e => setBlockForm(prev => ({ ...prev, reason: e.target.value }))} placeholder="e.g., Spring vacation" />
          </div>
        </div>
      </Modal>

      <style>{`
        .staff-settings { max-width: 900px; padding: 0; }
        .settings-header h1 { color: var(--text-primary); margin: 0 0 20px; font-size: 24px; }
        .settings-tabs {
          display: flex; gap: 4px; margin-bottom: 24px;
          border-bottom: 2px solid var(--border-secondary); padding-bottom: 0;
        }
        .settings-tab {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 20px; border: none; background: none;
          color: var(--text-secondary); font-size: 14px; font-weight: 500;
          cursor: pointer; border-bottom: 2px solid transparent;
          margin-bottom: -2px; transition: all 0.15s;
        }
        .settings-tab:hover { color: var(--text-primary); }
        .settings-tab.active { color: var(--accent-blue); border-bottom-color: var(--accent-blue); }
        .settings-error {
          background: #ef444415; border: 1px solid #ef444440; border-radius: var(--radius-medium);
          padding: 10px 16px; color: #ef4444; margin-bottom: 16px; font-size: 13px;
          display: flex; justify-content: space-between; align-items: center;
        }
        .settings-error button { background: none; border: none; color: #ef4444; cursor: pointer; text-decoration: underline; font-size: 12px; }
        .settings-loading { padding: 40px; text-align: center; color: var(--text-tertiary); }
        .settings-section {}
        .section-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
        .section-header h2 { color: var(--text-primary); margin: 0 0 4px; font-size: 18px; }
        .section-desc { color: var(--text-secondary); margin: 0; font-size: 13px; }
        .empty-message { color: var(--text-tertiary); font-size: 14px; font-style: italic; padding: 20px 0; }

        /* Availability grid */
        .availability-grid { display: flex; flex-direction: column; gap: 2px; }
        .day-row {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 10px 16px; background: var(--bg-primary);
          border-radius: var(--radius-medium);
        }
        .day-label { width: 36px; font-weight: 600; color: var(--text-secondary); font-size: 13px; padding-top: 4px; flex-shrink: 0; }
        .day-windows { display: flex; flex-wrap: wrap; gap: 8px; flex: 1; min-height: 28px; align-items: center; }
        .no-windows { color: var(--text-tertiary); font-size: 12px; font-style: italic; }
        .window-chip {
          display: flex; align-items: center; gap: 8px;
          padding: 4px 10px; background: var(--bg-secondary);
          border: 1px solid var(--border-secondary); border-radius: 16px; font-size: 13px;
        }
        .window-chip.inactive { opacity: 0.4; }
        .window-time { color: var(--text-primary); font-weight: 500; }
        .window-services { color: var(--text-tertiary); font-size: 11px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .window-actions { display: flex; gap: 2px; margin-left: 4px; }
        .window-actions button { background: none; border: none; color: var(--text-tertiary); cursor: pointer; padding: 2px; border-radius: 4px; }
        .window-actions button:hover { color: var(--text-primary); background: var(--bg-tertiary); }

        /* Overrides */
        .overrides-list { display: flex; flex-direction: column; gap: 6px; }
        .override-row {
          display: flex; align-items: center; gap: 12px;
          padding: 8px 16px; background: var(--bg-primary);
          border-radius: var(--radius-medium); font-size: 13px;
        }
        .override-badge { font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 500; }
        .override-badge.unavailable { background: #ef444420; color: #ef4444; }
        .override-badge.available { background: #10b98120; color: #10b981; }
        .override-date { color: var(--text-primary); font-weight: 500; }
        .override-time { color: var(--text-secondary); }
        .override-reason { color: var(--text-tertiary); font-style: italic; margin-left: auto; }
        .icon-btn { background: none; border: none; color: var(--text-tertiary); cursor: pointer; padding: 4px; border-radius: 4px; }
        .icon-btn:hover { color: #ef4444; background: var(--bg-secondary); }

        /* Blocked time */
        .blocks-list { display: flex; flex-direction: column; gap: 6px; }
        .block-row {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 16px; background: var(--bg-primary);
          border-radius: var(--radius-medium); font-size: 13px;
        }
        .block-type-badge { font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 500; text-transform: capitalize; }
        .block-type-badge.blocked { background: #ef444420; color: #ef4444; }
        .block-type-badge.vacation { background: #f59e0b20; color: #f59e0b; }
        .block-type-badge.personal { background: #8b5cf620; color: #8b5cf6; }
        .block-dates { color: var(--text-primary); font-weight: 500; }
        .block-reason { color: var(--text-tertiary); font-style: italic; margin-left: auto; }

        /* Slot preview */
        .preview-controls {
          display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
          padding: 12px 16px; background: var(--bg-primary); border-radius: var(--radius-medium);
        }
        .preview-controls label { font-size: 13px; font-weight: 600; color: var(--text-secondary); }
        .select-wrapper { position: relative; flex: 1; }
        .select-wrapper select {
          width: 100%; padding: 8px 32px 8px 12px; appearance: none;
          border: 1px solid var(--border-secondary); border-radius: var(--radius-medium);
          background: var(--bg-secondary); color: var(--text-primary); font-size: 14px;
        }
        .select-icon { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: var(--text-tertiary); pointer-events: none; }
        .preview-day { margin-bottom: 12px; }
        .preview-day-header { font-weight: 600; font-size: 14px; color: var(--text-primary); margin-bottom: 8px; padding-left: 4px; }
        .preview-slots { display: flex; flex-wrap: wrap; gap: 6px; }
        .slot-chip {
          padding: 4px 12px; background: #10b98115; border: 1px solid #10b98130;
          border-radius: 14px; font-size: 12px; color: #10b981; font-weight: 500;
        }

        /* Calendar sync */
        .sync-disconnected {
          text-align: center; padding: 48px 24px;
          background: var(--bg-primary); border: 2px dashed var(--border-secondary);
          border-radius: var(--radius-large);
        }
        .sync-icon { color: var(--text-tertiary); margin-bottom: 16px; }
        .sync-disconnected h3 { color: var(--text-primary); margin: 0 0 8px; }
        .sync-disconnected p { color: var(--text-secondary); max-width: 500px; margin: 0 auto 24px; font-size: 14px; }
        .connect-btn { text-decoration: none; display: inline-flex; }
        .sync-connected {}
        .sync-status-bar {
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
          padding: 16px 20px; background: var(--bg-primary); border-radius: var(--radius-large);
          border: 1px solid var(--border-secondary); margin-bottom: 20px;
        }
        .sync-badge { font-size: 11px; padding: 3px 10px; border-radius: 12px; font-weight: 600; text-transform: uppercase; }
        .sync-badge.connected { background: #10b98120; color: #10b981; }
        .sync-provider { font-weight: 600; color: var(--text-primary); }
        .sync-last { color: var(--text-tertiary); font-size: 12px; }
        .sync-actions-bar { margin-left: auto; display: flex; gap: 8px; }
        .danger-text { color: #ef4444; }
        .danger-text:hover { color: #ef4444; border-color: #ef444440; }
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .calendar-list { }
        .calendar-list h3 { color: var(--text-primary); font-size: 16px; margin: 0 0 4px; }
        .calendar-item {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 16px; background: var(--bg-primary);
          border-radius: var(--radius-medium); margin-top: 6px;
        }
        .calendar-info { display: flex; align-items: center; gap: 10px; }
        .calendar-name { font-weight: 500; color: var(--text-primary); font-size: 14px; }
        .mc-badge {
          font-size: 10px; padding: 2px 8px; border-radius: 10px;
          background: var(--accent-blue)15; color: var(--accent-blue); font-weight: 500;
        }

        /* Modal form styles */
        .modal-form { display: flex; flex-direction: column; gap: 14px; }
        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .form-group label { font-size: 13px; font-weight: 600; color: var(--text-secondary); }
        .form-group input, .form-group select, .form-group textarea {
          padding: 8px 12px; border: 1px solid var(--border-secondary); border-radius: var(--radius-medium);
          background: var(--bg-secondary); color: var(--text-primary); font-size: 14px;
        }
        .form-group input:focus, .form-group select:focus { outline: none; border-color: var(--accent-blue); }
        .form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .checkbox-label {
          display: flex; align-items: center; gap: 8px;
          font-size: 14px; color: var(--text-primary); cursor: pointer;
        }
        .day-checkboxes { display: flex; gap: 6px; }
        .day-check {
          width: 40px; height: 36px; border-radius: var(--radius-medium);
          border: 1px solid var(--border-secondary); background: var(--bg-secondary);
          color: var(--text-secondary); font-size: 12px; font-weight: 600;
          cursor: pointer; transition: all 0.15s;
        }
        .day-check.active { border-color: var(--accent-blue); background: rgba(59, 130, 246, 0.15); color: var(--accent-blue); }
        .day-shortcuts { display: flex; gap: 8px; margin-top: 6px; }
        .shortcut-btn {
          background: none; border: none; color: var(--text-tertiary);
          font-size: 11px; cursor: pointer; text-decoration: underline; padding: 0;
        }
        .shortcut-btn:hover { color: var(--text-primary); }
        .service-toggles { display: flex; flex-wrap: wrap; gap: 6px; }
        .service-toggle {
          padding: 4px 12px; border: 1px solid var(--border-secondary); border-radius: 14px;
          background: var(--bg-secondary); color: var(--text-secondary); font-size: 12px;
          cursor: pointer; transition: all 0.15s;
        }
        .service-toggle.active { border-color: var(--accent-blue); background: var(--accent-blue)15; color: var(--accent-blue); }
        .no-services { font-size: 12px; color: var(--text-tertiary); font-style: italic; }

        .btn { padding: 8px 16px; border-radius: var(--radius-medium); font-size: 14px; font-weight: 500; cursor: pointer; border: none; transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px; }
        .btn-primary { background: var(--accent-blue); color: white; }
        .btn-primary:hover { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-secondary { background: var(--bg-secondary); color: var(--text-secondary); border: 1px solid var(--border-secondary); }
        .btn-secondary:hover { background: var(--bg-tertiary); }
      `}</style>
    </div>
  );
}
